import React, { useEffect, useState, useMemo } from "react";
import { Box, Button, TextField, Typography, Grid2 } from "@mui/material";
import PropertyCard from "../../../../components/property-card/card"; // Import PropertyCard component
import Header from "../../../../components/Header/Header";
import Footer from "../../../../components/Footer/Footer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firestore-DB-config/firebase-config";

interface PropertyCardProps {
  id: string;
  image: string;
  price: string;
  title: string;
  address: string;
  beds: number;
  baths: number;
  label: string;
  labelColor: "primary" | "error" | "success";
  labelIcon: JSX.Element;
  type: string;
}

const PropertyListingPage: React.FC = () => {
  const [properties, setProperties] = useState<PropertyCardProps[]>([]);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Function to map labels to icons
  const getLabelIcon = (label: string): JSX.Element => {
    switch (label) {
      case "Newest":
        return <HomeIcon />;
      case "Discounted":
        return <AttachMoneyIcon />;
      case "Popular":
        return <WhatshotIcon />;
      default:
        return <HomeIcon />; // Default to HomeIcon if no match
    }
  };

  // Fetch properties from Firestore on component mount
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "properties"));
        const propertiesData = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id, // Firestore document ID
            image: data.imageUrls || "", // Provide default values if necessary
            price: data.price || "",
            title: data.title || "",
            address: data.address || "",
            beds: data.beds || 0,
            baths: data.baths || 0,
            label: data.label || "",
            labelColor: data.labelColor || "primary",
            labelIcon: getLabelIcon(data.label), // Handle labelIcon
            type: data.type || "",
          } as PropertyCardProps;
        });
        setProperties(propertiesData);
      } catch (error) {
        console.error("Error fetching properties: ", error);
      }
    };

    fetchProperties();
  }, []);

  // Memoize filtered properties for performance optimization
  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const matchesFilter = filter === "all" || property.type === filter;
      const matchesSearch = property.title
        .toLowerCase()
        .includes(searchQuery.toLowerCase());
      return matchesFilter && matchesSearch;
    });
  }, [properties, filter, searchQuery]);

  return (
    <>
      <Header />

      <Box
        sx={{
          maxWidth: "80%",
          margin: "40px auto",
          padding: "15px",
          backgroundColor: "white",
        }}
      >
        {/* Back button */}
        <Button
          onClick={() => window.history.back()}
          sx={{
            mb: 2,
            display: "inline-flex",
            alignItems: "center",
            color: "#3B3C4A",
            fontSize: "1rem",
          }}
          startIcon={<ArrowBackIcon sx={{ fontSize: 20 }} />}
        >
          Back
        </Button>

        {/* Filter and Search Section */}
        <Box
          display="flex"
          justifyContent="space-between"
          my={4}
          sx={{
            flexDirection: { xs: "column", md: "row" },
            alignItems: { sm: "stretch", md: "center" },
            gap: { xs: 2, md: 1 },
          }}
        >
          {/* Search Bar */}
          <TextField
            label="Search Here"
            variant="outlined"
            fullWidth
            sx={{
              maxWidth: { xs: "100%", sm: "65%" },
              borderRadius: "14px",
              "& .MuiOutlinedInput-root": {
                borderRadius: "14px",
              },
            }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          {/* Filter Buttons */}
          <Box
            display="flex"
            gap={1}
            sx={{
              mt: { xs: 2, sm: 0 },
              flexDirection: "row",
              alignItems: "center",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {["all", "For Sell", "For Rent"].map((type) => (
              <Button
                key={type}
                variant={filter === type ? "contained" : "outlined"}
                onClick={() => setFilter(type)}
                sx={{
                  padding: "16px 40px",
                  borderRadius: "14px",
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                {type === "all" ? "All" : type.split(" ")[1]}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Property Cards Grid */}
        <Grid2
          container
          spacing={{ xs: 4, sm: 8, md: 10 }}
          justifyContent={{ xs: "center", md: "left" }}
          marginTop={6}
        >
          {filteredProperties.length > 0 ? (
            filteredProperties.map((property) => (
              <Grid2 key={property.id} size={{ xs: 11, md: 6, lg: 4 }}>
                <PropertyCard prp={property} />
              </Grid2>
            ))
          ) : (
            <Typography
              variant="h4"
              sx={{ color: "#7A7A7A", my: 25, fontWeight: "600" }}
            >
              No properties match your search.
            </Typography>
          )}
        </Grid2>
      </Box>

      <Footer />
    </>
  );
};

export default PropertyListingPage;
